/* A small, self-contained rotary card wheel for the public site.
   It imitates the app's wheel (app/src/app.js: WHEEL, tilt(), place(), the spring loop and attachDrag)
   without importing it: cards hang on a rod, the front one lies flat, the ones behind lean back so their
   tabs show, and cards flip toward you as they pass. Drag, scroll, arrow keys, the buttons or a tab
   click turn it. prefers-reduced-motion makes every move instant (a short cross-fade instead).

   mountWheel(host, cards, {label}) → {go(i), step(d), index(), destroy()}
   A card: {kind, code, color (CSS colour or var()), tabX ("20%"), tab, title, sub, status, tone,
            fields:[[label, value]…], note, tags:[…], due, dueTone} */

var GEO = {
  lean: 1.36,          /* degrees each card behind leans further back */
  gap: .55,            /* a little extra between the front card and the first one behind */
  fall: 100,           /* degrees a card turns toward you as it flips past */
  fadeFrom: 4.4, fadeTo: 5.8
};
var SPRING = {K: 220, D: 26};
var PX_PER_CARD = 120, FRICTION = 90, MAX_V = 30;

function tilt(d){
  if(d >= 0) return d * GEO.lean + GEO.gap * Math.min(d, 1);
  return -(Math.min(-d, 1) * GEO.fall + Math.max(0, -d - 1) * 16);
}
function mod(a, n){ return ((a % n) + n) % n; }
function esc(s){ return String(s == null ? "" : s).replace(/[&<>"']/g, function(c){ return {"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[c]; }); }

function cardHTML(c){
  var fields = (c.fields || []).map(function(f){ return '<div><dt>' + esc(f[0]) + '</dt><dd>' + esc(f[1]) + '</dd></div>'; }).join("");
  var tags = (c.tags || []).map(function(t){ return '<span class="w-tag">#' + esc(t) + '</span>'; }).join("");
  return '<button type="button" class="w-tab" tabindex="-1" aria-label="Turn to ' + esc(c.kind + ": " + c.title) + '">' +
      '<span class="k">' + esc(c.code) + '</span><span class="n">' + esc(c.tab || c.title) + '</span><span class="w-tab-shade"></span></button>' +
    '<div class="w-body"><div class="w-face">' +
      '<div class="w-head"><div class="w-titles"><p class="w-title">' + esc(c.title) + '</p><p class="w-sub">' + esc(c.sub) + '</p></div>' +
        (c.status ? '<span class="w-pill ' + esc(c.tone || "") + '">' + esc(c.status) + '</span>' : "") + '</div>' +
      (fields ? '<dl class="w-fields">' + fields + '</dl>' : "") +
      (c.note ? '<p class="w-note">' + esc(c.note) + '</p>' : "") +
      '<div class="w-foot">' + tags + (c.due ? '<span class="w-due ' + esc(c.dueTone || "") + '">' + esc(c.due) + '</span>' : "") + '</div>' +
    '</div><div class="w-shade"></div></div>';
}

export function mountWheel(host, cards, opts){
  opts = opts || {};
  var n = cards.length;
  var mq = window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
  var reduce = !!(mq && mq.matches);

  host.classList.add("demo");
  host.innerHTML =
    '<div class="demo-stage" tabindex="0" role="region" aria-roledescription="card wheel" aria-label="' + esc(opts.label || "Example cards") + '. Use the arrow keys to turn the wheel.">' +
      '<div class="demo-deck"></div>' +
      '<div class="demo-rod" aria-hidden="true"></div><div class="demo-base" aria-hidden="true"></div>' +
      '<div class="demo-foot">' +
        '<button type="button" class="demo-btn" data-step="-1" aria-label="Previous card">&#8593;</button>' +
        '<span class="demo-count" aria-hidden="true"></span>' +
        '<button type="button" class="demo-btn" data-step="1" aria-label="Next card">&#8595;</button>' +
      '</div>' +
    '</div>' +
    '<p class="sr-only" aria-live="polite"></p>' +
    '<p class="demo-hint"><span>Drag or scroll to spin</span><span>Arrow keys work too</span><span>Click a tab to jump</span></p>';

  var stage = host.querySelector(".demo-stage");
  var deck = host.querySelector(".demo-deck");
  var count = host.querySelector(".demo-count");
  var live = host.querySelector("[aria-live]");
  var els = cards.map(function(c, i){
    var el = document.createElement("div");
    el.className = "w-card";
    el.style.setProperty("--tc", c.color);
    if(c.tabX) el.style.setProperty("--tab-x", c.tabX);
    el.innerHTML = cardHTML(c);
    el._shade = el.querySelector(".w-shade");
    el._tabShade = el.querySelector(".w-tab-shade");
    el._tab = el.querySelector(".w-tab");
    el._tab.addEventListener("click", function(e){ e.stopPropagation(); if(Date.now() - W.dragEnd < 80) return; go(i); });
    deck.appendChild(el);
    return el;
  });

  var W = {pos: 0, target: 0, vel: 0, raf: 0, last: 0, drag: null, dragEnd: 0, said: -1, snapT: 0};

  /* offset of card i from the front: 0 = front, + = behind, − = flipped past; the ring wraps the short way */
  function offset(i){
    var k = mod(i - W.pos, n);
    return k > n - 3 ? k - n : k;
  }
  function draw(){
    for(var i = 0; i < n; i++){
      var el = els[i], d = offset(i);
      var front = Math.abs(d) < .5;
      if(el._front !== front){ el._front = front; el.classList.toggle("front", front); el.setAttribute("aria-hidden", front ? "false" : "true"); }
      var tr = Math.abs(d) < 4e-4 ? "none" : "rotateX(" + tilt(d).toFixed(3) + "deg)";
      if(el._tr !== tr){ el._tr = tr; el.style.transform = tr; }
      var op = d < 0 ? Math.max(0, 1 + d * 1.05) : d > GEO.fadeFrom ? Math.max(0, (GEO.fadeTo - d) / (GEO.fadeTo - GEO.fadeFrom)) : 1;
      op = op < .004 ? 0 : Math.round(op * 1000) / 1000;
      if(el._op !== op){ el._op = op; el.style.opacity = op; el.style.visibility = op ? "" : "hidden"; }
      var pe = op > .3 && d > -.5 ? "auto" : "none";
      if(el._pe !== pe){ el._pe = pe; el._tab.style.pointerEvents = pe; }
      /* light falls from above: cards darken as they lean back and as they tip toward you */
      var sh = d > 0 ? Math.min(.3, d * .05) : Math.min(.4, -d * .5);
      sh = Math.round(sh * 500) / 500;
      if(el._sh !== sh){ el._sh = sh; el._shade.style.opacity = sh; el._tabShade.style.opacity = Math.min(.2, sh * .8); }
    }
  }
  function index(){ return mod(Math.round(W.target), n); }
  function settle(){
    W.vel = 0; W.last = 0;
    if(W.target !== Math.round(W.target)){ W.pos = W.target; draw(); return; }   /* mid-scroll: wait for the snap */
    W.pos = W.target = mod(W.target, n);
    draw();
    var i = index();
    stage.dataset.index = String(i);
    if(W.said !== i){
      W.said = i;
      var c = cards[i];
      live.textContent = "Card " + (i + 1) + " of " + n + ". " + c.kind + ": " + c.title + (c.sub ? ", " + c.sub : "") + ".";
    }
  }
  function frame(t){
    W.raf = 0;
    var dt = W.last ? Math.min((t - W.last) / 1000, 1 / 30) : 1 / 60;
    W.last = t;
    if(!W.drag){
      var steps = Math.max(1, Math.ceil(dt * 240)), h = dt / steps;
      for(var s = 0; s < steps; s++){
        var acc = -SPRING.K * (W.pos - W.target) - SPRING.D * W.vel;
        W.vel += acc * h; W.pos += W.vel * h;
      }
    }
    draw();
    if(W.drag || Math.abs(W.vel) > .01 || Math.abs(W.pos - W.target) > 1e-3) W.raf = requestAnimationFrame(frame);
    else settle();
  }
  function kick(){
    count.textContent = (index() + 1) + " / " + n;
    if(reduce){
      if(W.raf){ cancelAnimationFrame(W.raf); W.raf = 0; }
      var before = W.said;
      W.pos = W.target; settle();
      var el = els[index()];
      if(before !== W.said && el.animate) el.animate([{opacity: .25}, {opacity: 1}], {duration: 180, easing: "ease-out"});
      return;
    }
    if(!W.raf){ W.last = 0; W.raf = requestAnimationFrame(frame); }
  }
  function step(d){
    clearTimeout(W.snapT);
    W.target = Math.round(W.target) + d;
    kick();
  }
  /* the short way round to card i */
  function go(i){
    clearTimeout(W.snapT);
    var base = Math.round(W.target), cur = mod(base, n), d = mod(i - cur, n);
    if(d > n / 2) d -= n;
    W.target = base + d;
    kick();
  }

  /* keys: the stage is focusable */
  stage.addEventListener("keydown", function(e){
    if(e.altKey || e.ctrlKey || e.metaKey) return;
    var k = e.key, d = 0;
    if(k === "ArrowDown" || k === "ArrowRight" || k === "PageDown") d = 1;
    else if(k === "ArrowUp" || k === "ArrowLeft" || k === "PageUp") d = -1;
    else if(k === "Home"){ e.preventDefault(); go(0); return; }
    else if(k === "End"){ e.preventDefault(); go(n - 1); return; }
    if(d){ e.preventDefault(); step(d); }
  });
  host.querySelector(".demo-foot").addEventListener("click", function(e){
    var b = e.target.closest("[data-step]"); if(b) step(+b.dataset.step);
  });

  /* scroll wheel / trackpad: nudge the target fractionally so it follows your fingers, then snap */
  stage.addEventListener("wheel", function(e){
    var dy = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
    if(e.deltaMode === 1) dy *= 33; else if(e.deltaMode === 2) dy *= 400;
    if(!dy) return;
    e.preventDefault();
    clearTimeout(W.snapT);
    if(reduce){
      W.acc = (W.acc || 0) + dy;
      if(Math.abs(W.acc) >= 60){ step(W.acc > 0 ? 1 : -1); W.acc = 0; }
      W.snapT = setTimeout(function(){ W.acc = 0; }, 200);
      return;
    }
    W.target += Math.max(-1.5, Math.min(1.5, dy / 110));
    if(W.target - W.pos > 4) W.target = W.pos + 4;
    if(W.pos - W.target > 4) W.target = W.pos - 4;
    kick();
    W.snapT = setTimeout(function(){ W.target = Math.round(W.target); kick(); }, 130);
  }, {passive: false});

  /* drag: mouse pulls down (or sideways) to flip toward you; touch swipes sideways so vertical still scrolls the page */
  var g = null;
  function move(e){
    if(!g || e.pointerId !== g.id) return;
    var dx = e.clientX - g.x, dy = e.clientY - g.y;
    if(!g.on){
      if(Math.abs(dx) < 7 && Math.abs(dy) < 7) return;
      g.axis = g.touch ? "x" : (Math.abs(dy) >= Math.abs(dx) ? "y" : "x");
      if(g.touch && Math.abs(dy) > Math.abs(dx)){ end(); return; }
      g.on = true; g.p0 = W.pos; g.samples = [];
      g.x = e.clientX; g.y = e.clientY; dx = dy = 0;
      W.drag = g; W.vel = 0; clearTimeout(W.snapT);
      stage.classList.add("dragging");
      kick();
    }
    var p = g.p0 + (g.axis === "y" ? dy / PX_PER_CARD : -dx / PX_PER_CARD);
    g.samples.push([e.timeStamp, p]);
    while(g.samples.length > 2 && e.timeStamp - g.samples[0][0] > 90) g.samples.shift();
    g.last = p;
    if(!reduce){ W.pos = p; }
  }
  function end(){
    document.removeEventListener("pointermove", move);
    document.removeEventListener("pointerup", up);
    document.removeEventListener("pointercancel", up);
    g = null;
  }
  function up(e){
    if(!g || (e && e.pointerId !== g.id)) return;
    var was = g; end();
    if(!was.on) return;
    stage.classList.remove("dragging");
    W.dragEnd = Date.now();
    W.drag = null;
    if(reduce){
      var moved = was.last - was.p0, k = Math.abs(moved) > .35 ? Math.round(moved) || Math.sign(moved) : 0;
      if(k) step(k);
      return;
    }
    var sm = was.samples, v = 0;
    if(sm.length > 1){ var a = sm[0], b = sm[sm.length - 1], dt = (b[0] - a[0]) / 1000; if(dt > .004) v = (b[1] - a[1]) / dt; }
    if(e && sm.length && e.timeStamp - sm[sm.length - 1][0] > 120) v = 0;
    v = Math.max(-MAX_V, Math.min(MAX_V, v));
    W.vel = v;
    W.target = Math.round(W.pos + v * Math.abs(v) / (2 * FRICTION));
    kick();
  }
  stage.addEventListener("pointerdown", function(e){
    if(e.button > 0 || g) return;
    if(e.target.closest(".demo-foot")) return;
    g = {id: e.pointerId, x: e.clientX, y: e.clientY, on: false, touch: e.pointerType === "touch", samples: []};
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
    document.addEventListener("pointercancel", up);
  });
  function onReduce(e){ reduce = e.matches; if(reduce){ W.target = Math.round(W.target); kick(); } }
  if(mq){ if(mq.addEventListener) mq.addEventListener("change", onReduce); else if(mq.addListener) mq.addListener(onReduce); }

  /* first paint: a short turn into place, so it's clear the cards move (skipped for reduced motion) */
  var start = opts.start || 0;
  W.pos = reduce ? start : start - 2.2; W.target = start;
  draw(); kick();

  return {
    go: go, step: step, index: index,
    destroy: function(){ if(W.raf) cancelAnimationFrame(W.raf); end(); host.innerHTML = ""; }
  };
}
